import { useEffect, useRef, useCallback } from 'react'
import { get } from 'lodash'
import { kdTree as KDTree } from 'kd-tree-javascript'

const HOVER_DISTANCE_THRESHOLD = 0.5 // Increased to support Hit Layer at lower zooms



const getCoordKey = (lat, lng) => `${Number(lat).toFixed(5)}_${Number(lng).toFixed(5)}`

export const useBreadcrumbInteraction = ({ mapData, breadcrumbsByRouteId, routesById, timestampData }) => {
  const kdTreesRef = useRef({})

  // --- Build KDTrees (Data Processing) ---
  // --- Build KDTrees (Data Processing) ---
  useEffect(() => {
    // We need mapData (for structure/stops?) or just timestampData?
    // The previous code used mapData.routeLocationTimestamps
    // Now we prefer timestampData if available, fallback to mapData
    const rawData = timestampData || mapData || {}
    const routeLocationTimestamps = rawData?.routeLocationTimestamps || rawData

    if (!routeLocationTimestamps || !breadcrumbsByRouteId) return

    const trees = {}
    const distanceFn = (a, b) => Math.pow(a.lat - b.lat, 2) + Math.pow(a.lng - b.lng, 2)

    Object.keys(routeLocationTimestamps).forEach(routeId => {
      const timestamps = routeLocationTimestamps[routeId] || []
      const segments = breadcrumbsByRouteId[routeId] || []

      // 1. Create Driver Lookup Map from GeoJSON segments
      const coordDriverMap = new Map()
      segments.forEach(segment => {
        const driver = segment.driver || 'Unassigned'
        const coords = segment.coordinates || []
        coords.forEach(pt => {
          // Mapbox/GeoJSON is [lng, lat]
          const key = getCoordKey(pt[1], pt[0])
          coordDriverMap.set(key, driver)
        })
      })

      // 2. Process Points (Calculate Speed + Attach Driver)
      if (timestamps.length > 0) {
        // Sort points by timestamp to ensure chronological order
        const sortedPoints = [...timestamps].sort((a, b) => a.timestamp - b.timestamp).map(point => ({ ...point }))

        // Track the last known driver to fill in gaps where coordinates don't perfectly match
        let lastKnownDriver = routesById[routeId]?.driverName || 'Unassigned'

        const enrichedPoints = sortedPoints.map((point, index) => {
          let speed = 0
          if (point.speedMps !== undefined && point.speedMps !== null) {
            speed = point.speedMps * 2.23694
          }
          point.speed = speed

          // Attach Driver
          const key = getCoordKey(point.lat, point.lng)
          const exactMatchDriver = coordDriverMap.get(key)

          // Logic:
          // 1. If we found an exact match on the segment, use it and update history.
          // 2. If NO exact match (e.g. point was simplified out of the line), use the last known driver.
          if (exactMatchDriver) {
            lastKnownDriver = exactMatchDriver
            point.driver = exactMatchDriver
          } else {
            point.driver = lastKnownDriver
          }

          return point
        })

        trees[routeId] = new KDTree(enrichedPoints, distanceFn, ['lat', 'lng'])
      }
    })

    kdTreesRef.current = trees
  }, [mapData, breadcrumbsByRouteId, routesById, timestampData])

  // --- Interaction Helper ---
  const getNearestPoint = useCallback((routeId, lat, lng) => {
    const tree = kdTreesRef.current[routeId]
    if (!tree) return null

    const nearest = tree.nearest({ lat, lng }, 1)

    if (nearest && nearest[0] && nearest[0][1] < HOVER_DISTANCE_THRESHOLD) {
      return nearest[0][0] // Return the data object {lat, lng, speed, timestamp, driver}
    }
    return null
  }, [])

  return { getNearestPoint }
}
